import { parseDate } from '@openphone/lib/date'
import { action, makeAutoObservable, observable, toJS } from 'mobx'

import objectId from '@src/lib/objectId'
import type ContactStore from '@src/service/contact-store'
import type { Model } from '@src/service/model/base'
import type { GooglePeopleSyncProgressState } from '@src/service/transport/websocket'

import type { BaseCodableContactSettings } from './BaseCodableContactSettings'

export interface RawGoogleContactSettings extends BaseCodableContactSettings {
  hasRefreshToken: boolean | null
  source: string | null
  sourceName: string | null
  type: string | null
  resyncStatus: GooglePeopleSyncProgressState | null
}

export class GoogleContactSettingsModel implements RawGoogleContactSettings, Model {
  private raw: RawGoogleContactSettings

  constructor(private contactStore: ContactStore) {
    this.raw = {
      id: objectId(),
      createdAt: null,
      updatedAt: null,
      meta: null,
      hasRefreshToken: null,
      source: null,
      sourceName: null,
      type: null,
      resyncStatus: null,
      userId: null,
    }

    makeAutoObservable<this, 'raw'>(this, {
      raw: observable.deep,
    })
  }

  get id(): string {
    return this.raw.id
  }

  get createdAt(): number | null {
    return this.raw.createdAt
  }

  get updatedAt(): number | null {
    return this.raw.updatedAt
  }

  get meta(): { hasRefreshToken: boolean } | null {
    return this.raw.meta
  }

  get hasRefreshToken(): boolean | null {
    return this.raw.hasRefreshToken
  }

  get source(): string | null {
    return this.raw.source
  }

  get sourceName(): string | null {
    return this.raw.sourceName
  }

  get type(): string | null {
    return this.raw.type
  }

  get resyncStatus(): GooglePeopleSyncProgressState | null {
    return this.raw.resyncStatus
  }

  get userId(): string | null {
    return this.raw.userId
  }

  resync() {
    this.raw.resyncStatus = 'started'
    return this.contactStore.resyncGoogleContactSettings(this.serialize()).catch(
      action((error) => {
        this.raw.resyncStatus = null
        throw error
      }),
    )
  }

  localUpdate(attrs: Partial<RawGoogleContactSettings>) {
    this.raw = { ...this.raw, ...attrs }
    return this
  }

  deserialize = ({ updatedAt, createdAt, ...json }: RawGoogleContactSettings) => {
    this.raw = { ...this.raw, ...json }

    this.raw.updatedAt = updatedAt ? parseDate(updatedAt) : null
    this.raw.createdAt = createdAt ? parseDate(createdAt) : null

    return this
  }

  serialize = (): RawGoogleContactSettings => {
    return {
      createdAt: this.createdAt,
      id: this.id,
      meta: toJS(this.meta),
      hasRefreshToken: this.hasRefreshToken,
      source: this.source,
      sourceName: this.sourceName,
      type: this.type,
      updatedAt: this.updatedAt,
      userId: this.userId,
      resyncStatus: this.resyncStatus,
    }
  }
}
